// Transient microphone input. Browser dependencies are injectable for node:test.
export const AGENT_AUDIO_LIMITS = Object.freeze({ seconds: 120, minSeconds: 0.3, sampleRate: 16000, bytes: 44 + 120 * 16000 * 2 });
type Phase = 'idle' | 'requesting' | 'recording' | 'transcribing';
export interface AudioDependencies {
  secure(): boolean;
  getStream(): Promise<MediaStream>;
  recorder(stream: MediaStream): MediaRecorder;
  prepare(blob: Blob): Promise<Blob>;
  now(): number;
  tick(callback: () => void): ReturnType<typeof setInterval>;
  clearTick(handle: ReturnType<typeof setInterval>): void;
}

// Canonical WAV keeps the actual duration verifiable by the API, independently of caller metadata.
export function encodeVoiceWav(audio: Pick<AudioBuffer, 'length' | 'sampleRate' | 'numberOfChannels' | 'getChannelData'>): Blob {
  const rate = AGENT_AUDIO_LIMITS.sampleRate;
  const length = Math.min(Math.floor(audio.length * rate / audio.sampleRate), rate * AGENT_AUDIO_LIMITS.seconds);
  if (!(length >= rate * AGENT_AUDIO_LIMITS.minSeconds) || !audio.numberOfChannels) throw new Error('AUDIO_SHORT');
  const buffer = new ArrayBuffer(44 + length * 2), view = new DataView(buffer);
  const word = (offset: number, value: string) => { for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i)); };
  word(0, 'RIFF'); view.setUint32(4, buffer.byteLength - 8, true); word(8, 'WAVE'); word(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  word(36, 'data'); view.setUint32(40, length * 2, true);
  const channels = Array.from({ length: audio.numberOfChannels }, (_, channel) => audio.getChannelData(channel));
  for (let i = 0; i < length; i++) {
    // Average the source interval when downsampling, interpolate when upsampling.
    const begin = i * audio.sampleRate / rate, end = (i + 1) * audio.sampleRate / rate;
    let value = 0, weight = 0;
    for (let j = Math.floor(begin); j < Math.ceil(end); j++) {
      const fraction = Math.min(end, j + 1) - Math.max(begin, j);
      if (fraction <= 0) continue;
      for (const channel of channels) value += (channel[Math.min(j, channel.length - 1)] || 0) * fraction;
      weight += fraction * channels.length;
    }
    value = Math.max(-1, Math.min(1, value / weight));
    view.setInt16(44 + i * 2, Math.round(value * (value < 0 ? 32768 : 32767)), true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

export function browserAudioDependencies(): AudioDependencies {
  return {
    secure: () => window.isSecureContext,
    getStream: async () => {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') throw new Error('AUDIO_UNSUPPORTED');
      return navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
    },
    recorder: stream => {
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(type => MediaRecorder.isTypeSupported(type));
      return new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    },
    prepare: async blob => {
      const Context = window.AudioContext || (window as any).webkitAudioContext;
      if (!Context) throw new Error('AUDIO_UNSUPPORTED');
      const context: AudioContext = new Context();
      try { return encodeVoiceWav(await context.decodeAudioData(await blob.arrayBuffer())); }
      finally { await context.close(); }
    },
    now: () => Date.now(), tick: callback => setInterval(callback, 250), clearTick: handle => clearInterval(handle)
  };
}

export function audioError(error: any): string {
  const code = error?.error?.error?.code || error?.code || error?.message || error?.name;
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') return 'Autorisez l’accès au micro dans votre navigateur puis réessayez.';
  if (error?.name === 'NotFoundError' || error?.name === 'NotReadableError') return 'Micro indisponible. Vérifiez qu’il est connecté et disponible.';
  if (code === 'AUDIO_HTTPS') return 'Le micro nécessite une connexion HTTPS. Ouvrez Aglo avec son adresse sécurisée.';
  if (code === 'AUDIO_UNSUPPORTED') return 'Ce navigateur ne permet pas l’enregistrement audio. Essayez un navigateur récent.';
  if (code === 'AUDIO_SHORT') return 'L’enregistrement est trop court. Dictez votre demande avant de l’arrêter.';
  if (code === 'AUDIO_EMPTY') return 'Aucune parole reconnue. Vérifiez votre micro puis recommencez.';
  if (code === 'AUDIO_TOO_LONG' || error?.status === 413) return 'La demande est trop longue. Raccourcissez le texte ou l’enregistrement.';
  if (error?.status === 401) return 'Votre session a expiré. Reconnectez-vous pour transcrire votre demande.';
  if (error?.status === 403 || error?.status === 404) return 'Cette conversation n’est plus accessible. Ouvrez une autre conversation.';
  if (code === 'QUOTA_EXCEEDED') return 'Le budget quotidien de l’agent est atteint. Réessayez demain.';
  if (code === 'AUDIO_RATE_LIMIT' || error?.status === 429) return 'Le service de transcription est occupé. Réessayez dans un instant.';
  if (code === 'AUDIO_TIMEOUT') return 'La transcription a pris trop de temps. Vous pouvez réessayer cet enregistrement.';
  return 'La transcription n’a pas abouti. Votre texte est conservé ; vous pouvez réessayer.';
}

export class AgentVoiceInput {
  phase: Phase = 'idle';
  elapsedSeconds = 0;
  error = '';
  notice = '';
  private stream?: MediaStream;
  private recording?: MediaRecorder;
  private interval?: ReturnType<typeof setInterval>;
  private controller?: AbortController;
  private audio?: Blob;
  private prepared = false;
  private epoch = 0;
  private disposed = false;
  constructor(private transcribe: (audio: Blob, signal: AbortSignal) => Promise<void>, private changed: () => void,
    private deps: AudioDependencies = browserAudioDependencies()) {}
  get busy() { return this.phase !== 'idle'; }
  get canRetry() { return Boolean(this.audio) && !this.busy; }
  get timeLabel() { return `${Math.floor(this.elapsedSeconds / 60)}:${String(this.elapsedSeconds % 60).padStart(2, '0')}`; }

  async start() {
    if (this.busy || this.disposed) return;
    this.cancel(); const epoch = this.epoch;
    if (!this.deps.secure()) { this.error = audioError(new Error('AUDIO_HTTPS')); this.changed(); return; }
    this.phase = 'requesting'; this.changed();
    try {
      const stream = await this.deps.getStream();
      if (epoch !== this.epoch || this.disposed) { stream.getTracks().forEach(track => track.stop()); return; }
      this.stream = stream;
      const recorder = this.recording = this.deps.recorder(stream), chunks: Blob[] = [];
      recorder.ondataavailable = event => { if (epoch === this.epoch && event.data.size) chunks.push(event.data); };
      recorder.onerror = () => {
        if (epoch !== this.epoch) return;
        this.cancel(); this.error = 'L’enregistrement a été interrompu. Vérifiez votre micro puis recommencez.'; this.changed();
      };
      recorder.onstop = () => {
        if (epoch !== this.epoch || this.disposed) return;
        this.release();
        this.audio = new Blob(chunks, { type: recorder.mimeType });
        void this.process(epoch);
      };
      const started = this.deps.now();
      recorder.start(250); this.phase = 'recording';
      this.interval = this.deps.tick(() => {
        this.elapsedSeconds = Math.min(AGENT_AUDIO_LIMITS.seconds, Math.floor((this.deps.now() - started) / 1000));
        if (this.elapsedSeconds >= AGENT_AUDIO_LIMITS.seconds) { this.notice = 'Limite de deux minutes atteinte.'; this.stop(); }
        this.changed();
      });
      this.changed();
    } catch (error) {
      if (epoch !== this.epoch || this.disposed) return;
      this.release(); this.recording = undefined; this.phase = 'idle'; this.error = audioError(error); this.changed();
    }
  }
  stop() {
    if (this.phase !== 'recording' || !this.recording) return;
    this.phase = 'transcribing'; this.recording.stop(); this.release(); this.changed();
  }
  async retry() { if (this.canRetry) { this.error = ''; await this.process(this.epoch); } }
  private async process(epoch: number) {
    this.phase = 'transcribing'; this.error = ''; const controller = this.controller = new AbortController(); this.changed();
    try {
      let audio = this.audio!;
      if (!this.prepared) audio = await this.deps.prepare(audio);
      if (epoch !== this.epoch || this.disposed) return;
      this.audio = audio; this.prepared = true;
      await this.transcribe(audio, controller.signal);
      if (epoch !== this.epoch || this.disposed) return;
      this.audio = undefined; this.notice = 'Demande dictée ajoutée au texte. Relisez-la avant de l’envoyer.';
    } catch (error) {
      if (epoch !== this.epoch || this.disposed) return;
      this.error = audioError(error);
    } finally { if (epoch === this.epoch && !this.disposed) { this.controller = undefined; this.phase = 'idle'; this.changed(); } }
  }
  private release() {
    if (this.interval !== undefined) this.deps.clearTick(this.interval);
    this.interval = undefined;
    this.stream?.getTracks().forEach(track => track.stop()); this.stream = undefined;
  }
  cancel() {
    this.epoch++; this.controller?.abort(); this.controller = undefined;
    if (this.recording) {
      this.recording.onstop = null; this.recording.ondataavailable = null; this.recording.onerror = null;
      if (this.recording.state !== 'inactive') this.recording.stop();
    }
    this.recording = undefined; this.release(); this.audio = undefined; this.prepared = false;
    this.phase = 'idle'; this.error = ''; this.notice = ''; this.elapsedSeconds = 0;
    if (!this.disposed) this.changed();
  }
  dispose() { this.disposed = true; this.cancel(); }
}
